import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { ConfirmModal } from '../common/modal'
import Pagination from '../common/pagination'
import ScanReview from '../common/scan-review'
import { deleteDocument, getDocumentList, scanDocument, uploadDocuments } from '../../redux/actions/documentAction'
import { getItemOptions } from '../../redux/actions/itemAction'
import { docDownloadUrl, docViewUrl } from '../../config/config'
import { FILE_TYPE_ICON, MAX_FILE_SIZE_MB, fileTypeFromName, formatBytes, formatDate, getErrorMessage } from '../../utils'

const LIMIT = 10
const MAX_FILES = 10
const POLL_MS = 3000

// Scan status badge for one document
const ScanStatus = ({ extraction = {} }) => {
    switch (extraction.status) {
        case 'processing':
            return <span className="Scan Scan-processing"><i className="fa fa-spinner fa-spin"></i> Scanning</span>
        case 'done':
            if (extraction.importedCount > 0) return <span className="Scan Scan-imported">{extraction.importedCount} saved</span>
            return extraction.needsReview
                ? <span className="Scan Scan-review">{extraction.itemCount} items · review</span>
                : <span className="Scan Scan-done">{extraction.itemCount} items</span>
        case 'failed':
            return (
                <>
                    <span className="Scan Scan-failed">Failed</span>
                    <small className="ScanError">{extraction.error}</small>
                </>
            )
        case 'unsupported':
            return <small className="text-muted">Not scannable</small>
        default:
            return <small className="text-muted">Not scanned</small>
    }
}

const DocUpload = () => {
    const dispatch = useDispatch()
    const { documentList, loader, uploading, progress } = useSelector(state => state.document)
    const { itemOptions } = useSelector(state => state.item)

    const inputRef = useRef(null)
    const [files, setFiles] = useState([])
    const [title, setTitle] = useState('')
    const [item, setItem] = useState('')
    const [dragOver, setDragOver] = useState(false)

    const [search, setSearch] = useState('')
    const [fileType, setFileType] = useState('')
    const [page, setPage] = useState(1)
    const [toDelete, setToDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [reviewId, setReviewId] = useState(null)
    const closeReview = useCallback(() => setReviewId(null), [])

    const load = useCallback(() => {
        dispatch(getDocumentList({ page, limit: LIMIT, search: search || undefined, fileType: fileType || undefined }))
            .catch(err => toast.error(getErrorMessage(err)))
    }, [dispatch, page, search, fileType])

    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    useEffect(() => {
        dispatch(getItemOptions()).catch(() => {})
    }, [dispatch])

    // Refresh quietly while any document on this page is still being scanned
    const scanning = (documentList.result || []).some(d => d.extraction?.status === 'processing')
    useEffect(() => {
        if (!scanning) return
        const t = setInterval(() => {
            dispatch(getDocumentList({ page, limit: LIMIT, search: search || undefined, fileType: fileType || undefined })).catch(() => {})
        }, POLL_MS)
        return () => clearInterval(t)
    }, [scanning, dispatch, page, search, fileType])

    const handleScan = async (doc) => {
        try {
            const res = await dispatch(scanDocument(doc._id))
            toast.info(res.message)
            load()
        } catch (err) {
            toast.error(getErrorMessage(err))
        }
    }

    const addFiles = (list) => {
        const incoming = Array.from(list || [])
        const accepted = []
        incoming.forEach(file => {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                toast.error(`${file.name}: file is larger than ${MAX_FILE_SIZE_MB} MB`)
            } else {
                accepted.push(file)
            }
        })
        setFiles(prev => {
            const merged = [...prev]
            accepted.forEach(f => {
                if (!merged.some(m => m.name === f.name && m.size === f.size)) merged.push(f)
            })
            if (merged.length > MAX_FILES) toast.warn(`You can upload up to ${MAX_FILES} files at once`)
            return merged.slice(0, MAX_FILES)
        })
        if (inputRef.current) inputRef.current.value = ''
    }

    const onDrop = (e) => {
        e.preventDefault()
        setDragOver(false)
        addFiles(e.dataTransfer.files)
    }

    const handleUpload = async () => {
        if (!files.length) return toast.error('Please choose at least one file')
        try {
            const res = await dispatch(uploadDocuments({ files, title: files.length === 1 ? title.trim() : '', item }))
            toast.success(res.message)
            setFiles([])
            setTitle('')
            setItem('')
            if (page !== 1) setPage(1)
            else load()
        } catch (err) {
            toast.error(getErrorMessage(err, 'Upload failed'))
        }
    }

    const handleDelete = async () => {
        setDeleting(true)
        try {
            const res = await dispatch(deleteDocument(toDelete._id))
            toast.success(res.message)
            setToDelete(null)
            if (documentList.result?.length === 1 && page > 1) setPage(page - 1)
            else load()
        } catch (err) {
            toast.error(getErrorMessage(err))
        } finally {
            setDeleting(false)
        }
    }

    const rows = documentList.result || []

    return (
        <>
            <div className="Small-Wrapper">
                <div className="TitleBox">
                    <h2>Doc Upload</h2>
                </div>

                <div
                    className={`DropZone ${dragOver ? 'DragOver' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
                >
                    <i className="fa fa-cloud-upload" aria-hidden="true"></i>
                    <h4>Drag &amp; drop files here, or <span>browse</span></h4>
                    <p>Any file type · up to {MAX_FILE_SIZE_MB} MB each · max {MAX_FILES} files</p>
                    <p className="ScanHint">Images, PDFs, Word and Excel files are scanned for items automatically</p>
                    <div className="TypeChips">
                        {['image', 'pdf', 'word', 'excel', 'other'].map(t => <i key={t} className={`fa ${FILE_TYPE_ICON[t]} FileIcon-${t}`}></i>)}
                    </div>
                    <input ref={inputRef} type="file" multiple hidden onChange={e => addFiles(e.target.files)} />
                </div>

                {files.length > 0 && (
                    <div className="SelectedFiles">
                        <ul>
                            {files.map((file, i) => (
                                <li key={`${file.name}-${file.size}`}>
                                    <i className={`fa ${FILE_TYPE_ICON[fileTypeFromName(file.name)]} FileIcon FileIcon-${fileTypeFromName(file.name)}`}></i>
                                    <span className="FileName">{file.name}</span>
                                    <small>{formatBytes(file.size)}</small>
                                    <button type="button" className="IconBtn Red" title="Remove" disabled={uploading}
                                        onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>
                                        <i className="fa fa-times"></i>
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <div className="row CommonForm">
                            {files.length === 1 && (
                                <div className="col-md-5 form-group">
                                    <label>Document Title (optional)</label>
                                    <input className="form-control w-100" value={title} onChange={e => setTitle(e.target.value)}
                                        placeholder={files[0].name.replace(/\.[^.]+$/, '')} />
                                </div>
                            )}
                            <div className="col-md-5 form-group">
                                <label>Link to Item (optional)</label>
                                <select className="form-control w-100" value={item} onChange={e => setItem(e.target.value)}>
                                    <option value="">— None —</option>
                                    {itemOptions.map(opt => (
                                        <option key={opt._id} value={opt._id}>{opt.name}{opt.companyName ? ` (${opt.companyName})` : ''}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {uploading && (
                            <div className="progress mb-3">
                                <div className="progress-bar" style={{ width: `${progress}%` }}>{progress}%</div>
                            </div>
                        )}

                        <div>
                            <button className="Button" onClick={handleUpload} disabled={uploading}>
                                <i className="fa fa-upload"></i> {uploading ? 'Uploading...' : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
                            </button>
                            <button className="Button Cancel ml-2" onClick={() => setFiles([])} disabled={uploading}>Clear</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="Small-Wrapper">
                <div className="TitleBox">
                    <h2>Uploaded Documents</h2>
                </div>

                <div className="Filter">
                    <div className="form-group">
                        <label>Search</label>
                        <input type="search" className="form-control" placeholder="Title or file name"
                            value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
                    </div>
                    <div className="form-group">
                        <label>File Type</label>
                        <select className="form-control" value={fileType} onChange={e => { setFileType(e.target.value); setPage(1) }}>
                            <option value="">All</option>
                            <option value="image">Images</option>
                            <option value="pdf">PDF</option>
                            <option value="word">Word</option>
                            <option value="excel">Excel / CSV</option>
                            <option value="text">Text</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>&nbsp;</label>
                        <button className="Button Cancel" title="Reset filters" onClick={() => { setSearch(''); setFileType(''); setPage(1) }}>
                            <i className={`fa fa-refresh ${loader ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                <div className="TableList">
                    <table>
                        <thead>
                            <tr>
                                <th>S.No.</th>
                                <th>Preview</th>
                                <th>Title</th>
                                <th>File Name</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th>Linked Item</th>
                                <th>Scan Status</th>
                                <th>Uploaded</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((doc, i) => {
                                const viewable = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'].includes(doc.mimeType)
                                const ex = doc.extraction || {}
                                const canScan = ex.status && ex.status !== 'unsupported' && ex.status !== 'processing'
                                return (
                                    <tr key={doc._id}>
                                        <td>{(page - 1) * LIMIT + i + 1}</td>
                                        <td>
                                            {viewable && doc.fileType === 'image'
                                                ? <img className="Thumb" src={docViewUrl(doc._id)} alt={doc.title} />
                                                : <i className={`fa ${FILE_TYPE_ICON[doc.fileType]} FileIcon FileIcon-${doc.fileType} FileIconLg`}></i>}
                                        </td>
                                        <td>{doc.title}</td>
                                        <td className="text-break">{doc.originalName}</td>
                                        <td className="text-uppercase">{doc.extension || '-'}</td>
                                        <td>{formatBytes(doc.size)}</td>
                                        <td>{doc.item?.name || '-'}</td>
                                        <td><ScanStatus extraction={ex} /></td>
                                        <td>{formatDate(doc.createdAt)}</td>
                                        <td>
                                            <div className="Actions">
                                                {ex.status === 'done' && (
                                                    <button className="IconBtn Blue" title="Review items" onClick={() => setReviewId(doc._id)}>
                                                        <i className="fa fa-list-alt"></i>
                                                    </button>
                                                )}
                                                {canScan && (
                                                    <button className="IconBtn Purple" title={ex.status === 'none' ? 'Scan for items' : 'Scan again'} onClick={() => handleScan(doc)}>
                                                        <i className="fa fa-magic"></i>
                                                    </button>
                                                )}
                                                {viewable && (
                                                    <a className="Blue" title="View" href={docViewUrl(doc._id)} target="_blank" rel="noreferrer">
                                                        <i className="fa fa-eye"></i>
                                                    </a>
                                                )}
                                                <a className="Green" title="Download" href={docDownloadUrl(doc._id)}>
                                                    <i className="fa fa-download"></i>
                                                </a>
                                                <button className="IconBtn Red" title="Delete" onClick={() => setToDelete(doc)}>
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                            {!rows.length && (
                                <tr><td colSpan="10">{loader && !rows.length ? 'Loading...' : 'No documents uploaded yet.'}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="ListFooter">
                    <span>Total: {documentList.total || 0}</span>
                    <Pagination page={page} pages={documentList.pages || 1} onChange={setPage} />
                </div>
            </div>

            <ScanReview docId={reviewId} onClose={closeReview} onImported={load} />

            <ConfirmModal
                show={!!toDelete}
                title="Delete Document"
                message={`Delete "${toDelete?.originalName}"? The file will be removed from the server.`}
                busy={deleting}
                onCancel={() => setToDelete(null)}
                onConfirm={handleDelete}
            />
        </>
    )
}

export default DocUpload
